import { DiffEditor, Editor } from '@monaco-editor/react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'material-react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import {
  getExperiment,
  getExperimentChange,
  getExperimentHistory,
} from '../../services/experiments';
import { experimentsUIUrl } from '../../variables/Pages';
import { timePickerTheme } from '../../utilities/Styles';

function ExperimentHistoryComponent() {
  const { id } = useParams<'id'>();
  const [experimentHistory, setExperimentHistory] = React.useState<any[]>([]);
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const [expandedData, setExpandedData] = React.useState<{
    [key: string]: any;
  }>({});
  const [pageSize, setPageSize] = React.useState<number>(10);
  const [nextPageToken, setNextPageToken] = React.useState<string | null>(null);
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [debouncedStartDate, setDebouncedStartDate] =
    React.useState<Date | null>(null);
  const [debouncedEndDate, setDebouncedEndDate] = React.useState<Date | null>(
    null,
  );
  const [startDateError, setStartDateError] = React.useState<string | null>(
    null,
  );
  const [endDateError, setEndDateError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = React.useState<boolean>(true);
  const [selectedChangelogs, setSelectedChangelogs] = React.useState<
    Set<string>
  >(new Set());
  const [comparisonData, setComparisonData] = React.useState<{
    original: any;
    modified: any;
    fromDate: string;
    toDate: string;
    fromChange: any;
    toChange: any;
    fromSide: 'from' | 'to';
    toSide: 'from' | 'to';
  } | null>(null);
  const [comparing, setComparing] = React.useState<boolean>(false);
  const [showComparisonDialog, setShowComparisonDialog] =
    React.useState<boolean>(false);
  const defaultFromSide: 'from' | 'to' = 'from';
  const defaultToSide: 'from' | 'to' = 'to';

  // Validate date
  const isValidDate = (date: Date | null): boolean => {
    if (!date) return true; // null is valid (no filter)
    return date instanceof Date && !Number.isNaN(date.getTime());
  };

  // Debounce start date
  useEffect(() => {
    if (startDate === null) {
      // Clear date - no error, update immediately
      setStartDateError(null);
      setDebouncedStartDate(null);
      return undefined;
    }
    if (!isValidDate(startDate)) {
      setStartDateError('Invalid date');
      return undefined;
    }
    setStartDateError(null);

    const timer = setTimeout(() => {
      setDebouncedStartDate(startDate);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [startDate]);

  // Debounce end date
  useEffect(() => {
    if (endDate === null) {
      // Clear date - no error, update immediately
      setEndDateError(null);
      setDebouncedEndDate(null);
      return undefined;
    }
    if (!isValidDate(endDate)) {
      setEndDateError('Invalid date');
      return undefined;
    }
    setEndDateError(null);

    const timer = setTimeout(() => {
      setDebouncedEndDate(endDate);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [endDate]);

  const fetchExperimentHistory = useCallback(
    async (pageToken?: string | null) => {
      if (!id) return;

      const isLoadMore = !!pageToken;
      const hasDateFilter =
        (debouncedStartDate && isValidDate(debouncedStartDate)) ||
        (debouncedEndDate && isValidDate(debouncedEndDate));

      if (isLoadMore) {
        setLoadingMore(true);
      } else if (isInitialLoad) {
        // Only show full loading spinner on initial load
        setLoading(true);
      }

      try {
        const options: {
          page_size?: number;
          page_token?: string;
          start_date?: string;
          end_date?: string;
        } = {
          // If date filter is set, use a large page size to load all results
          page_size: hasDateFilter ? 1000 : pageSize,
        };

        if (pageToken) {
          options.page_token = pageToken;
        }

        if (debouncedStartDate && isValidDate(debouncedStartDate)) {
          // Set to start of day (00:00:00) and format as ISO 8601
          const startDateTime = new Date(debouncedStartDate);
          startDateTime.setHours(0, 0, 0, 0);
          options.start_date = startDateTime.toISOString();
        }

        if (debouncedEndDate && isValidDate(debouncedEndDate)) {
          // Set to end of day (23:59:59) and format as ISO 8601
          const endDateTime = new Date(debouncedEndDate);
          endDateTime.setHours(23, 59, 59, 999);
          options.end_date = endDateTime.toISOString();
        }

        const result = await getExperimentHistory(id, options);

        if (isLoadMore) {
          setExperimentHistory((prev) => [...prev, ...(result.changes || [])]);
        } else {
          setExperimentHistory(result.changes || []);
        }

        const nextToken = result.nextPageToken || null;
        setNextPageToken(nextToken);
        setIsInitialLoad(false);
      } catch (error) {
        toast.error(`Error fetching experiment history: ${error}`);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [id, pageSize, debouncedStartDate, debouncedEndDate, isInitialLoad],
  );

  // Auto-load all pages when date filter is set
  useEffect(() => {
    const hasDateFilter =
      (debouncedStartDate && isValidDate(debouncedStartDate)) ||
      (debouncedEndDate && isValidDate(debouncedEndDate));

    if (hasDateFilter && nextPageToken && !loading && !loadingMore) {
      // Small delay to prevent overwhelming the backend
      const timer = setTimeout(() => {
        fetchExperimentHistory(nextPageToken);
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [
    nextPageToken,
    debouncedStartDate,
    debouncedEndDate,
    loading,
    loadingMore,
    fetchExperimentHistory,
  ]);

  useEffect(() => {
    setIsInitialLoad(true);
    fetchExperimentHistory();
  }, [id]);

  useEffect(() => {
    if (!isInitialLoad) {
      fetchExperimentHistory();
    }
  }, [fetchExperimentHistory, isInitialLoad]);

  const handleLoadMore = () => {
    if (nextPageToken) {
      fetchExperimentHistory(nextPageToken);
    }
  };

  const handleChangelogSelect = (date: string) => {
    setSelectedChangelogs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else if (newSet.size < 2) {
        // Only allow selecting if less than 2 are selected
        newSet.add(date);
      }
      return newSet;
    });
  };

  const handleCompare = async () => {
    if (selectedChangelogs.size !== 2 || !id) return;

    setComparing(true);
    try {
      // Determine which is older and newer based on dates
      const dates = Array.from(selectedChangelogs).sort();
      const [olderDate, newerDate] = dates;

      // Fetch both changes
      const [olderChange, newerChange] = await Promise.all([
        getExperimentChange(id, olderDate),
        getExperimentChange(id, newerDate),
      ]);

      // Compare using selected sides (default: older.from vs newer.to)
      setComparisonData({
        original: olderChange.change[defaultFromSide],
        modified: newerChange.change[defaultToSide],
        fromDate: olderDate,
        toDate: newerDate,
        fromChange: olderChange,
        toChange: newerChange,
        fromSide: defaultFromSide,
        toSide: defaultToSide,
      });
      setShowComparisonDialog(true);
    } catch (error) {
      toast.error(`Error comparing changelogs: ${error}`);
    } finally {
      setComparing(false);
    }
  };

  const handleAccordionChange =
    (panel: string, panelDate: string) =>
    async (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panelDate : false);
      if (isExpanded) {
        if (!expandedData[panelDate]) {
          const result = await getExperimentChange(panel, panelDate);
          setExpandedData((prevData) => ({
            ...prevData,
            [panelDate]: result,
          }));
        }
      }
    };

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          gutterBottom
          component='div'
          sx={{ color: 'text.primary' }}
          variant='h6'
        >
          Experiment Change History
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <FormControl size='small' sx={{ minWidth: 150 }}>
            <InputLabel>Page Size</InputLabel>
            <Select
              value={pageSize}
              label='Page Size'
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={timePickerTheme}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label='Start Date'
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  renderInput={(
                    params: JSX.IntrinsicAttributes & TextFieldProps,
                  ) => (
                    <TextField
                      {...params}
                      size='small'
                      sx={{ minWidth: 180 }}
                      error={!!startDateError}
                      helperText={startDateError}
                      onBlur={(e) => {
                        // Validate on blur
                        const inputValue = e.target.value;
                        if (inputValue && !startDate) {
                          const parsedDate = new Date(inputValue);
                          if (!isValidDate(parsedDate)) {
                            setStartDateError('Invalid date format');
                          }
                        }
                      }}
                    />
                  )}
                />
                <DatePicker
                  label='End Date'
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  renderInput={(
                    params: JSX.IntrinsicAttributes & TextFieldProps,
                  ) => (
                    <TextField
                      {...params}
                      size='small'
                      sx={{ minWidth: 180 }}
                      error={!!endDateError}
                      helperText={endDateError}
                      onBlur={(e) => {
                        // Validate on blur
                        const inputValue = e.target.value;
                        if (inputValue && !endDate) {
                          const parsedDate = new Date(inputValue);
                          if (!isValidDate(parsedDate)) {
                            setEndDateError('Invalid date format');
                          }
                        }
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </StyledEngineProvider>
          {(debouncedStartDate || debouncedEndDate) &&
            experimentHistory.length >= 2 && (
              <Button
                variant='outlined'
                size='small'
                onClick={async () => {
                  // Select first and last changelogs
                  const sortedDates = [...experimentHistory]
                    .map((item) => item.date)
                    .sort();
                  const firstDate = sortedDates[0];
                  const lastDate = sortedDates[sortedDates.length - 1];

                  setSelectedChangelogs(new Set([firstDate, lastDate]));

                  // Automatically trigger comparison
                  setComparing(true);
                  try {
                    const [olderChange, newerChange] = await Promise.all([
                      getExperimentChange(id!, firstDate),
                      getExperimentChange(id!, lastDate),
                    ]);

                    setComparisonData({
                      original: olderChange.change[defaultFromSide],
                      modified: newerChange.change[defaultToSide],
                      fromDate: firstDate,
                      toDate: lastDate,
                      fromChange: olderChange,
                      toChange: newerChange,
                      fromSide: defaultFromSide,
                      toSide: defaultToSide,
                    });
                    setShowComparisonDialog(true);
                  } catch (error) {
                    toast.error(`Error comparing changelogs: ${error}`);
                  } finally {
                    setComparing(false);
                  }
                }}
                disabled={comparing || experimentHistory.length < 2}
              >
                Compare First & Last
              </Button>
            )}
        </Box>
        {selectedChangelogs.size > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {selectedChangelogs.size} of 2 changelogs selected
              </Typography>
              {selectedChangelogs.size > 0 && (
                <Button
                  variant='outlined'
                  size='small'
                  onClick={() => {
                    setSelectedChangelogs(new Set());
                  }}
                >
                  Clear Selection
                </Button>
              )}
            </Box>
            {selectedChangelogs.size === 2 && (
              <Button
                variant='contained'
                onClick={handleCompare}
                disabled={comparing}
              >
                {comparing ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Comparing...
                  </>
                ) : (
                  'Compare Selected'
                )}
              </Button>
            )}
          </Box>
        )}
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {experimentHistory?.map((item: any, index: number) => {
            return (
              <Accordion
                key={index as any}
                expanded={expanded === item.date}
                onChange={handleAccordionChange(item.name, item.date)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      alignItems: 'center',
                    }}
                  >
                    <Checkbox
                      checked={selectedChangelogs.has(item.date)}
                      onChange={() => handleChangelogSelect(item.date)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={
                        !selectedChangelogs.has(item.date) &&
                        selectedChangelogs.size >= 2
                      }
                      sx={{ mr: 1 }}
                    />
                    <Typography
                      sx={{
                        width: '220px',
                        flexShrink: 0,
                        fontSize: '0.875rem',
                        color: 'text.secondary',
                      }}
                    >
                      {new Date(item.date).toLocaleDateString(
                        navigator.language || 'en-US',
                        {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: navigator.language?.startsWith('en'),
                        },
                      )}
                      <br />
                      <Typography
                        component='span'
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.disabled',
                        }}
                      >
                        {Intl.DateTimeFormat().resolvedOptions().timeZone}
                      </Typography>
                    </Typography>
                    <Divider flexItem orientation='vertical' sx={{ mx: 2 }} />
                    <Typography
                      sx={{
                        color: 'text.primary',
                        flex: 1,
                        textAlign: 'left',
                        fontSize: '0.9rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {item.message || 'N/A'}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {expandedData[item.date] ? (
                    <DiffEditor
                      height='90vh'
                      language='json'
                      modified={JSON.stringify(
                        expandedData[item.date].change.to,
                        null,
                        2,
                      )}
                      options={{ readOnly: true }}
                      original={JSON.stringify(
                        expandedData[item.date].change.from,
                        null,
                        2,
                      )}
                      keepCurrentOriginalModel={true}
                      keepCurrentModifiedModel={true}
                      theme='vs-dark'
                      width='100%'
                    />
                  ) : (
                    <CircularProgress />
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
          {nextPageToken && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant='outlined'
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? <CircularProgress size={24} /> : 'Load More'}
              </Button>
            </Box>
          )}
        </>
      )}
      <Dialog
        open={showComparisonDialog}
        onClose={() => setShowComparisonDialog(false)}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant='h6' component='div'>
                Compare Changelogs
              </Typography>
            </Box>
            <IconButton
              onClick={() => setShowComparisonDialog(false)}
              sx={{ ml: 2 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {comparisonData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    sx={{ color: 'text.secondary', fontWeight: 'medium' }}
                  >
                    Older Configuration
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FormControl size='small' sx={{ minWidth: 180 }}>
                      <Select
                        value={comparisonData.fromSide}
                        onChange={async (e) => {
                          const newFromSide = e.target.value as 'from' | 'to';
                          setComparisonData({
                            ...comparisonData,
                            original:
                              comparisonData.fromChange.change[newFromSide],
                            fromSide: newFromSide,
                          });
                        }}
                        renderValue={(value) => {
                          return value === 'from'
                            ? 'Settings prior to'
                            : 'Settings on';
                        }}
                      >
                        <MenuItem value='from'>Settings prior to</MenuItem>
                        <MenuItem value='to'>Settings on</MenuItem>
                      </Select>
                    </FormControl>
                    <Box sx={{ minWidth: 150 }}>
                      {(() => {
                        const item = experimentHistory.find(
                          (h) => h.date === comparisonData.fromDate,
                        );
                        return item ? (
                          <>
                            <Typography
                              variant='body2'
                              sx={{ color: 'text.primary' }}
                            >
                              {new Date(item.date).toLocaleDateString(
                                navigator.language || 'en-US',
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: navigator.language?.startsWith('en'),
                                },
                              )}
                            </Typography>
                            <Typography
                              variant='caption'
                              sx={{
                                fontSize: '0.75rem',
                                color: 'text.disabled',
                                display: 'block',
                              }}
                            >
                              {Intl.DateTimeFormat().resolvedOptions().timeZone}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant='body2'
                            sx={{ color: 'text.primary' }}
                          >
                            {comparisonData.fromDate}
                          </Typography>
                        );
                      })()}
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    sx={{ color: 'text.secondary', fontWeight: 'medium' }}
                  >
                    Newer Configuration
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FormControl size='small' sx={{ minWidth: 180 }}>
                      <Select
                        value={comparisonData.toSide}
                        onChange={async (e) => {
                          const newToSide = e.target.value as 'from' | 'to';
                          setComparisonData({
                            ...comparisonData,
                            modified: comparisonData.toChange.change[newToSide],
                            toSide: newToSide,
                          });
                        }}
                        renderValue={(value) => {
                          return value === 'from'
                            ? 'Settings prior to'
                            : 'Settings on';
                        }}
                      >
                        <MenuItem value='from'>Settings prior to</MenuItem>
                        <MenuItem value='to'>Settings on</MenuItem>
                      </Select>
                    </FormControl>
                    <Box sx={{ minWidth: 150 }}>
                      {(() => {
                        const item = experimentHistory.find(
                          (h) => h.date === comparisonData.toDate,
                        );
                        return item ? (
                          <>
                            <Typography
                              variant='body2'
                              sx={{ color: 'text.primary' }}
                            >
                              {new Date(item.date).toLocaleDateString(
                                navigator.language || 'en-US',
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: navigator.language?.startsWith('en'),
                                },
                              )}
                            </Typography>
                            <Typography
                              variant='caption'
                              sx={{
                                fontSize: '0.75rem',
                                color: 'text.disabled',
                                display: 'block',
                              }}
                            >
                              {Intl.DateTimeFormat().resolvedOptions().timeZone}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant='body2'
                            sx={{ color: 'text.primary' }}
                          >
                            {comparisonData.toDate}
                          </Typography>
                        );
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <DiffEditor
                height='80vh'
                language='json'
                modified={JSON.stringify(comparisonData.modified, null, 2)}
                options={{ readOnly: true }}
                original={JSON.stringify(comparisonData.original, null, 2)}
                keepCurrentOriginalModel={true}
                keepCurrentModifiedModel={true}
                theme='vs-dark'
                width='100%'
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowComparisonDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default function ExperimentDetail() {
  const navigate = useNavigate();
  const { id } = useParams<'id'>();
  const [experiment, setExperiment] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  useEffect(() => {
    const fetchExperiment = async () => {
      try {
        if (id) {
          const result = await getExperiment(id);
          setExperiment(JSON.stringify(result, null, 2));
        }
      } catch (error) {
        toast.error(`Error fetching experiment: ${error}`);
      } finally {
        setLoading(false);
      }
    };

    fetchExperiment();
  }, [id]);

  if (!experiment) {
    return <div />;
  }
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          maxWidth: '1200px',
          width: '100%',
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          gutterBottom
          component='div'
          sx={{ color: 'text.primary' }}
          variant='h5'
        >
          Experiment Details
        </Typography>

        <Typography
          gutterBottom
          component='div'
          sx={{ color: 'text.secondary' }}
          variant='h6'
        >
          {id}
        </Typography>
        <Button
          onClick={() => navigate(`/experiments/${id}/edit`)}
          size='small'
        >
          Edit
        </Button>
        <Typography gutterBottom component='div' sx={{ color: 'text.primary' }}>
          Current Experiment Configuration
        </Typography>
        <Button
          onClick={() => navigate(experimentsUIUrl)}
          startIcon={<ArrowBackIcon />}
          variant='outlined'
        >
          Back to Experiment List
        </Button>

        <Editor
          defaultLanguage='json'
          defaultValue={experiment}
          height='90vh'
          options={{ readOnly: true }}
          theme='vs-dark'
          value={experiment}
          width='100%'
        />
        {loading && <CircularProgress />}
        {experiment && (
          <Box mt={4} width='100%'>
            <ExperimentHistoryComponent />
          </Box>
        )}
      </Box>
    </Box>
  );
}
